import { lazy, Suspense } from 'react';
import { Navigate, useRoutes } from 'react-router-dom';
import { Box, CircularProgress } from '@mui/material';

// Layouts залишаємо звичайними імпортами для миттєвого рендеру каркаса
import DashboardLayout from './layouts/dashboard';
import SimpleLayout from './layouts/simple';

import profile from './store/profile';

// ----------------------------------------------------------------------
// Lazy-loading для всіх сторінок (Code Splitting)
// ----------------------------------------------------------------------

// Teacher Pages
const UserPage = lazy(() => import('./pages/teacher/UserPage'));
const GroupPage = lazy(() => import('./pages/teacher/GroupPage'));
const ChatPage = lazy(() => import('./pages/teacher/ChatPage'));
const ProductsPage = lazy(() => import('./pages/teacher/ProductsPage'));
const CoursesPage = lazy(() => import('./pages/teacher/CoursesPage'));
const CalendarPage = lazy(() => import('./pages/teacher/CalendarPage'));
const IntroPage = lazy(() => import('./pages/teacher/IntroPage'));
const Page404teacher = lazy(() => import('./pages/teacher/Page404teacher'));

// Student Pages
const IntroStudPage = lazy(() => import('./pages/student/IntroStudPage'));
const SelfstudyPage = lazy(() => import('./pages/student/SelfstudyPage'));
const ProfilePage = lazy(() => import('./pages/student/ProfilePage'));
const Homeworks = lazy(() => import('./pages/student/Homeworks'));
const MediaPage = lazy(() => import('./pages/student/MediaPage'));
const DayPage = lazy(() => import('./pages/student/DayPage'));
const DaylyPage = lazy(() => import('./pages/student/CalendarPage'));
const InfoPage = lazy(() => import('./pages/student/InfoPage'));
const Page404student = lazy(() => import('./pages/student/Page404student'));

// Auth & Common Pages
const LoginPage = lazy(() => import('./pages/LoginPage'));
const LoginPageST = lazy(() => import('./pages/LoginPageST'));
const Page404 = lazy(() => import('./pages/Page404'));

// Індикатор завантаження під час переходу між сторінками
const LoadingFallback = () => (
  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', width: '100%' }}>
    <CircularProgress />
  </Box>
);

// ----------------------------------------------------------------------

export default function Router() {
  const routes = useRoutes([
    profile.role === 2
      ? // teacher
        profile.data && profile.data.groups > 0
        ? {
            path: '/teacher',
            element: <DashboardLayout />,
            children: [
              { element: <Navigate to="/teacher/products" />, index: true },
              { path: 'intro', element: <IntroPage /> },
              { path: 'groups', element: <GroupPage /> },
              { path: 'chats', element: <ChatPage /> },
              { path: 'user', element: <UserPage /> },
              { path: 'products', element: <ProductsPage /> },
              { path: 'courses', element: <CoursesPage /> },
              { path: 'calendar', element: <CalendarPage /> },
              { path: 'profile', element: <ProfilePage /> },
              { path: 'day', element: <DayPage /> },
              { path: 'info', element: <InfoPage /> },
              {
                element: <SimpleLayout />,
                children: [
                  { path: '/teacher/*', element: <Navigate to="/teacher/404" /> },
                  { path: '/teacher/404', element: <Page404teacher /> },
                ],
              },
            ],
          }
        : // teacher without studio: only show intro
          {
            path: '/teacher',
            element: <DashboardLayout />,
            children: [
              { element: <Navigate to="/teacher/intro" />, index: true },
              { path: 'intro', element: <IntroPage /> },
              {
                element: <SimpleLayout />,
                children: [
                  { path: '/teacher/*', element: <Navigate to="/teacher/intro" /> },
                ],
              },
            ],
          }
      : profile.role === 1
      ? {
          path: '/student',
          element: <DashboardLayout />,
          children: [
            { element: <Navigate to="/student/profile" />, index: true },
            { path: 'profile', element: <ProfilePage /> },
            { path: 'selfstudy', element: <SelfstudyPage /> },
            { path: 'intro', element: <IntroStudPage /> },
            { path: 'homeworks', element: <Homeworks /> },
            { path: 'media', element: <MediaPage /> },
            { path: 'day', element: <DayPage /> },
            { path: 'calendar', element: <DaylyPage /> },
            { path: 'info', element: <InfoPage /> },
            {
              element: <SimpleLayout />,
              children: [
                { path: '/student/*', element: <Navigate to="/student/404" /> },
                { path: '/student/404', element: <Page404student /> },
              ],
            },
          ],
        }
      : { path: '/student/*', element: <Navigate to="/student/login" /> },
    {
      path: '/teacher/login',
      element: <LoginPage />,
    },
    {
      path: '/student/login',
      element: <LoginPageST />,
    },
    {
      element: <SimpleLayout />,
      children: [
        {
          element: profile.token ? (
            profile.role === 2 ? (
              <Navigate to="/teacher/intro" replace />
            ) : profile.role === 1 ? (
              <Navigate to="/student/profile" replace />
            ) : (
              <Navigate to="/student/login" replace />
            )
          ) : (
            <Navigate to="/student/login" replace />
          ),
          index: true,
        },
        { path: '/404', element: <Page404 /> },
        { path: '*', element: <Navigate to="/404" /> },
      ],
    },
    {
      path: '*',
      element: <Navigate to="/404" replace />,
    },
  ]);

  return <Suspense fallback={<LoadingFallback />}>{routes}</Suspense>;
}